import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Broom, Trash, ShoppingCart, Receipt, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SectionHeader } from "../components/shared";
import { Button } from "../components/ui/button";

export default function DraftCleanup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["draft-documents"],
    queryFn: async () => (await api.get("/draft-documents")).data,
    enabled: user?.role === "admin",
  });
  const items = data?.items || [];
  const selKeys = Object.keys(selected).filter((k) => selected[k]);

  const del = useMutation({
    mutationFn: async () => (await api.post("/draft-documents/delete", {
      items: selKeys.map((k) => { const [type, id] = k.split("::"); return { type, id }; }),
    })).data,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["draft-documents"] });
      qc.invalidateQueries({ queryKey: ["sales-orders"] });
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      setSelected({}); setConfirmOpen(false);
      toast.success(`${r.deleted} draft${r.deleted === 1 ? "" : "s"} removed`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const keyOf = (it) => `${it.type}::${it.id}`;
  const toggle = (it) => setSelected((s) => ({ ...s, [keyOf(it)]: !s[keyOf(it)] }));
  const allSelected = items.length > 0 && items.every((it) => selected[keyOf(it)]);
  const toggleAll = () => { if (allSelected) setSelected({}); else { const n = {}; items.forEach((it) => { n[keyOf(it)] = true; }); setSelected(n); } };
  const openOf = (it) => it.type === "sales" ? `/sales/doc/${it.id}` : `/purchasing/doc/${it.id}`;

  if (user?.role !== "admin") {
    return (
      <div className="animate-fade-up border border-border bg-card p-10 text-center">
        <Broom size={40} className="mx-auto text-muted-foreground mb-3" />
        <h2 className="text-lg font-bold">Administrators only</h2>
        <p className="text-sm text-muted-foreground mt-1">You need the admin role to clean up drafts.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-up max-w-4xl" data-testid="draft-cleanup-page">
      <SectionHeader title="Draft Cleanup" subtitle="Unfinished documents auto-saved with no line items" />

      <div className="border border-border bg-card rounded-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-primary h-4 w-4" data-testid="draft-select-all" disabled={!items.length} />
            {selKeys.length > 0 ? `${selKeys.length} selected` : `${items.length} empty draft${items.length === 1 ? "" : "s"}`}
          </label>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={selKeys.length === 0 || del.isPending}
            className="rounded-sm h-8 text-xs bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="draft-delete-selected"
          >
            <Trash size={14} className="mr-1.5" /> Delete selected
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16"><div className="h-7 w-7 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="py-14 text-center" data-testid="draft-empty">
            <Broom size={38} className="mx-auto text-muted-foreground mb-3" />
            <h3 className="font-bold">Nothing to clean up</h3>
            <p className="text-sm text-muted-foreground mt-1">There are no empty, unfinished draft documents.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((it) => (
              <li key={keyOf(it)} className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/30" data-testid="draft-row">
                <input type="checkbox" checked={!!selected[keyOf(it)]} onChange={() => toggle(it)} className="accent-primary h-4 w-4" data-testid={`draft-check-${it.id}`} />
                {it.type === "sales" ? <Receipt size={18} className="text-blue-500 shrink-0" /> : <ShoppingCart size={18} className="text-primary shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{it.doc_no}</span>
                    <span className="text-[10px] uppercase font-bold text-muted-foreground border border-border px-1.5 py-0.5 rounded-sm">{it.type === "sales" ? "Sales" : "Purchase"} {it.doc_type}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{it.party_name || "No party"}{it.created_at ? ` · created ${String(it.created_at).slice(0, 10)}` : ""}</p>
                </div>
                <button onClick={() => navigate(openOf(it))} className="text-muted-foreground hover:text-primary p-1" title="Open" data-testid={`draft-open-${it.id}`}><ArrowSquareOut size={16} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="draft-confirm">
          <div className="bg-card border border-border rounded-sm max-w-sm w-full p-5">
            <h3 className="font-bold text-lg">Delete {selKeys.length} draft{selKeys.length === 1 ? "" : "s"}?</h3>
            <p className="text-sm text-muted-foreground mt-1">These empty documents will be permanently removed. This cannot be undone.</p>
            <div className="flex gap-2 justify-end mt-4">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-sm" data-testid="draft-confirm-cancel">Cancel</Button>
              <Button onClick={() => del.mutate()} disabled={del.isPending} className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white" data-testid="draft-confirm-delete">{del.isPending ? "Deleting…" : "Delete"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
